/**
 * ClassScheduleModal — create a new class with recurrence.
 *
 * Validates min/max bounds against ``useCalendarConfig()`` (which mirrors
 * /v1/config/calendar). The backend is the source of truth — this is a
 * best-effort early warning so the operator isn't surprised by a 422.
 */

import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { toZonedTime } from 'date-fns-tz';

import { Button, Input, Modal } from '@/components/common';
import { useAuth } from '@/context';
import { ApiError } from '@core/api/client';
import { useCalendarConfig } from '@features/calendar/api/config';
import { useCreateClass, type ClassCreate } from '@features/calendar/api/classes';
import { useTeacherOptions, useSubjectOptions } from '@features/calendar/api/lookups';
import { CALENDAR_UI } from '@features/calendar/config/ui';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const ISO_DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/** Format a Date already projected into tenant tz as ``YYYY-MM-DD``. */
function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Build the initial form values for a tenant timezone. */
function buildEmptyForm(tenantTz: string) {
  // Compute "today" and "today + 28d" as wall-clock dates in the tenant tz,
  // NOT in the browser's local tz — otherwise a tenant in IST near midnight
  // UTC would see yesterday's date defaulted.
  const todayTz = toZonedTime(new Date(), tenantTz);
  const plus28 = new Date(
    todayTz.getFullYear(),
    todayTz.getMonth(),
    todayTz.getDate() + 28,
  );
  return {
    name: '',
    subject_id: '',
    teacher_id: '',
    capacity: 5,
    start_time: '17:00',
    end_time: '18:00',
    timezone: tenantTz,
    start_date: fmtDate(todayTz),
    end_date: fmtDate(plus28),
    days_of_week: [1, 3] as number[],
  };
}

export const ClassScheduleModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const tenantTz = user?.tenant_timezone ?? 'Asia/Kolkata';
  const [form, setForm] = useState(() => buildEmptyForm(tenantTz));
  const { rules } = useCalendarConfig();
  const { data: teachers = [] } = useTeacherOptions();
  const { data: subjects = [] } = useSubjectOptions();
  const createMut = useCreateClass();

  useEffect(() => {
    if (isOpen) setForm(buildEmptyForm(tenantTz));
  }, [isOpen, tenantTz]);

  const toggleDay = (d: number) =>
    setForm((s) => ({
      ...s,
      days_of_week: s.days_of_week.includes(d)
        ? s.days_of_week.filter((x) => x !== d)
        : [...s.days_of_week, d].sort((a, b) => a - b),
    }));

  const validate = (): string | null => {
    if (!form.name.trim()) return 'Class name is required';
    if (!form.subject_id) return 'Select a subject';
    if (!form.teacher_id) return 'Select a teacher';
    if (form.days_of_week.length === 0) return 'Pick at least one day';
    if (form.capacity < rules.class_capacity_min || form.capacity > rules.class_capacity_max) {
      return `Capacity must be between ${rules.class_capacity_min} and ${rules.class_capacity_max}`;
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const payload: ClassCreate = {
      name: form.name.trim(),
      subject_id: form.subject_id,
      teacher_id: form.teacher_id,
      capacity: form.capacity,
      status: 'active',
      branch_id: null,
      room_id: null,
      recurrence: {
        frequency: 'weekly',
        days_of_week: form.days_of_week,
        start_time: form.start_time,
        end_time: form.end_time,
        timezone: form.timezone,
        start_date: form.start_date,
        end_date: form.end_date,
        exceptions: [],
      },
    };
    try {
      await createMut.mutateAsync(payload);
      toast.success('Class created');
      onClose();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not create class');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New class"
      size="lg"
      data-testid="candeza-calendar-class-modal"
    >
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <Input
          label="Class name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="candeza-calendar-class-name-input"
        />
        <Input
          label="Capacity"
          type="number"
          min={rules.class_capacity_min}
          max={rules.class_capacity_max}
          value={String(form.capacity)}
          onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })}
          data-testid="candeza-calendar-class-capacity-input"
        />

        <div>
          <label
            style={{ display: 'block', fontSize: 13, marginBottom: 4, fontWeight: 500 }}
          >
            Subject
          </label>
          <select
            value={form.subject_id}
            onChange={(e) => setForm({ ...form, subject_id: e.target.value })}
            data-testid="candeza-calendar-class-subject-select"
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: 14,
            }}
          >
            <option value="">— Select subject —</option>
            {subjects.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            style={{ display: 'block', fontSize: 13, marginBottom: 4, fontWeight: 500 }}
          >
            Teacher
          </label>
          <select
            value={form.teacher_id}
            onChange={(e) => setForm({ ...form, teacher_id: e.target.value })}
            data-testid="candeza-calendar-class-teacher-select"
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: 14,
            }}
          >
            <option value="">— Select teacher —</option>
            {teachers.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <Input
          label="Start time"
          type="time"
          value={form.start_time}
          onChange={(e) => setForm({ ...form, start_time: e.target.value })}
          data-testid="candeza-calendar-class-start-time-input"
        />
        <Input
          label="End time"
          type="time"
          value={form.end_time}
          onChange={(e) => setForm({ ...form, end_time: e.target.value })}
          data-testid="candeza-calendar-class-end-time-input"
        />
        <Input
          label="Start date"
          type="date"
          value={form.start_date}
          onChange={(e) => setForm({ ...form, start_date: e.target.value })}
          data-testid="candeza-calendar-class-start-date-input"
        />
        <Input
          label="End date"
          type="date"
          value={form.end_date}
          onChange={(e) => setForm({ ...form, end_date: e.target.value })}
          data-testid="candeza-calendar-class-end-date-input"
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <label
          style={{ display: 'block', fontSize: 13, marginBottom: 8, fontWeight: 500 }}
        >
          Repeat on
        </label>
        <div
          data-testid="candeza-calendar-class-days-list"
          style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}
        >
          {ISO_DAYS.map((d, idx) => {
            const checked = form.days_of_week.includes(d);
            return (
              <button
                key={d}
                type="button"
                data-testid={`candeza-calendar-class-day-${d}`}
                data-active={checked}
                onClick={() => toggleDay(d)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 999,
                  border: `1px solid ${checked ? '#6366f1' : '#cbd5e1'}`,
                  background: checked ? '#6366f1' : '#f8fafc',
                  color: checked ? 'white' : '#0f172a',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {CALENDAR_UI.WEEKDAY_LABELS[idx]}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}
      >
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={submit}
          isLoading={createMut.isPending}
          data-testid="candeza-calendar-class-submit-btn"
        >
          Create class
        </Button>
      </div>
    </Modal>
  );
};

